'use client';

import { StatusBadge } from '@/components/common';
import { type Product, type RelatedIndustry } from '@/domain/products/types';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { DataGrid, GridColDef } from '@andritzot/metris-web-ui/mui-x/data-grid';

interface ManagersSectionProps {
  product: Product;
}

const managerTemplate = (rowData: RelatedIndustry, subject: string) => (
  <>
    <span>{rowData.manager?.name}</span>
    {rowData.manager?.email && (
      <>
        <span> - </span>
        <Box
          component="a"
          href={`mailto:${rowData.manager?.email}?subject=${encodeURIComponent(subject)}`}
          sx={{ textDecoration: 'none' }}
          target="_blank"
          rel="noreferrer"
        >
          {rowData.manager?.email}
        </Box>
      </>
    )}
  </>
);

export const hasManagerContacts = (product: Product) =>
  !!product.productOwner || !!product.productManager || !!product.technologyManager;

const ManagersSection = ({ product }: ManagersSectionProps) => {
  const managers = product.industries.filter((i) => !!i.manager);
  const { productOwner, productManager, technologyManager } = product;
  const subject = `Automation Solution Finder - ${product.product}`;
  const columns: GridColDef<RelatedIndustry>[] = [
    { field: 'industry', headerName: 'Industry', width: 150 },
    {
      field: 'manager.name',
      headerName: 'Manager',
      valueGetter: (_value, row) => row.manager?.name ?? '',
      renderCell: (params) => managerTemplate(params.row, subject),
    },
  ];

  if (hasManagerContacts(product)) {
    return (
      <Box sx={{ mb: 1 }}>
        <Typography variant="h4" sx={{ mb: 0 }}>
          Product Management
        </Typography>
        {productOwner && (
          <Box sx={{ mt: 0, mr: 0, mb: '10px', ml: '10px', display: 'flex', alignItems: 'center' }}>
            <Typography component="p" sx={{ mb: 0 }}>
              <b>Technical Product Owner</b>: <span>{productOwner.name}</span>
              {productOwner.email && (
                <>
                  <span> - </span>
                  <Box
                    component="a"
                    href={`mailto:${productOwner.email}?subject=${encodeURIComponent(subject)}`}
                    sx={{ textDecoration: 'none' }}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {productOwner.email}
                  </Box>
                </>
              )}
            </Typography>

            <Box sx={{ ml: 2 }}>
              <StatusBadge color={product.poApproved ? 'green' : 'yellow'}>
                {product.poApproved ? 'released' : 'pending release'}
              </StatusBadge>
            </Box>
          </Box>
        )}
        {technologyManager && (
          <Box sx={{ mt: 0, mr: 0, mb: '10px', ml: '10px', display: 'flex', alignItems: 'center' }}>
            <Typography component="p" sx={{ mb: 0 }}>
              <b>Head of Technology</b>: <span>{technologyManager.name}</span>
              {technologyManager.email && (
                <>
                  <span> - </span>
                  <Box
                    component="a"
                    href={`mailto:${technologyManager.email}?subject=${encodeURIComponent(subject)}`}
                    sx={{ textDecoration: 'none' }}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {technologyManager.email}
                  </Box>
                </>
              )}
            </Typography>
          </Box>
        )}
        {productManager && (
          <Box sx={{ mt: 0, mr: 0, mb: '10px', ml: '10px', display: 'flex', alignItems: 'center' }}>
            <Typography component="p" sx={{ mb: 0 }}>
              <b>Global Product Manager</b>: <span>{productManager.name}</span>
              {productManager.email && (
                <>
                  <span> - </span>
                  <Box
                    component="a"
                    href={`mailto:${productManager.email}?subject=${encodeURIComponent(subject)}`}
                    sx={{ textDecoration: 'none' }}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {productManager.email}
                  </Box>
                </>
              )}
            </Typography>
            <Box sx={{ ml: 2 }}>
              <StatusBadge color={product.pmApproved ? 'green' : 'yellow'}>
                {product.pmApproved ? 'released' : 'pending release'}
              </StatusBadge>
            </Box>
          </Box>
        )}

        {managers.length > 0 && (
          <DataGrid rows={managers} columns={columns} density="compact" getRowId={(row) => row.idIndustry} />
        )}
      </Box>
    );
  }
  return null;
};

export default ManagersSection;
